import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api';
import { ResponsiveList } from '../components/ResponsiveList';

const BRISBANE_TZ = 'Australia/Brisbane';

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-surface)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-lg)',
  padding: 'var(--spacing-4)',
  boxShadow: 'var(--shadow-sm)',
};

const kpiCardStyle: React.CSSProperties = {
  ...cardStyle,
  textAlign: 'center',
  flex: '1 1 180px',
  minWidth: 150,
};

const inputStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  backgroundColor: 'var(--color-background)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-default)',
  color: 'var(--color-text)',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
  marginBottom: 'var(--spacing-1)',
};

const presetButtonStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderRadius: '999px',
  border: '1px solid var(--color-border)',
  backgroundColor: 'var(--color-surface)',
  color: 'var(--color-text)',
  fontWeight: 600,
  fontSize: 'var(--font-size-sm)',
  cursor: 'pointer',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '2px solid var(--color-border)',
  fontSize: 'var(--font-size-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  color: 'var(--color-text-muted)',
};

const tdStyle: React.CSSProperties = {
  padding: 'var(--spacing-2) var(--spacing-3)',
  borderBottom: '1px solid var(--color-border)',
  fontSize: 'var(--font-size-sm)',
};

function formatDateStr(date: Date) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: BRISBANE_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date);
}

function getToday() {
  return formatDateStr(new Date());
}

function addDays(dateString: string, days: number) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(date.getDate() + days);
  return formatDateStr(date);
}

function startOfWeek(dateString: string) {
  const date = new Date(`${dateString}T12:00:00`);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return formatDateStr(date);
}

function endOfWeek(dateString: string) {
  return addDays(startOfWeek(dateString), 6);
}

function startOfMonth(dateString: string) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setDate(1);
  return formatDateStr(date);
}

function endOfMonth(dateString: string) {
  const date = new Date(`${dateString}T12:00:00`);
  date.setMonth(date.getMonth() + 1, 0);
  return formatDateStr(date);
}

type RangePreset =
  | 'TODAY'
  | 'THIS_WEEK'
  | 'LAST_WEEK'
  | 'THIS_MONTH'
  | 'LAST_MONTH'
  | 'LAST_30'
  | 'CUSTOM';

const PRESET_LABELS: Record<RangePreset, string> = {
  TODAY: 'Today',
  THIS_WEEK: 'This Week',
  LAST_WEEK: 'Last Week',
  THIS_MONTH: 'This Month',
  LAST_MONTH: 'Last Month',
  LAST_30: 'Last 30 Days',
  CUSTOM: 'Custom',
};

function rangeFromPreset(preset: RangePreset) {
  const today = getToday();
  switch (preset) {
    case 'TODAY':
      return { startDate: today, endDate: today };
    case 'THIS_WEEK':
      return { startDate: startOfWeek(today), endDate: endOfWeek(today) };
    case 'LAST_WEEK': {
      const ws = startOfWeek(today);
      const lws = addDays(ws, -7);
      return { startDate: lws, endDate: addDays(lws, 6) };
    }
    case 'THIS_MONTH':
      return { startDate: startOfMonth(today), endDate: endOfMonth(today) };
    case 'LAST_MONTH': {
      const cms = startOfMonth(today);
      const lmd = addDays(cms, -1);
      return { startDate: startOfMonth(lmd), endDate: endOfMonth(lmd) };
    }
    case 'LAST_30':
      return { startDate: addDays(today, -29), endDate: today };
    case 'CUSTOM':
    default:
      return { startDate: addDays(today, -29), endDate: today };
  }
}

const PRESETS: RangePreset[] = [
  'TODAY',
  'THIS_WEEK',
  'LAST_WEEK',
  'THIS_MONTH',
  'LAST_MONTH',
  'LAST_30',
  'CUSTOM',
];

const statusColor: Record<string, string> = {
  BOOKED: 'var(--color-accent)',
  COMPLETED: '#48bb78',
  CANCELLED: 'var(--color-danger, #e53e3e)',
  NO_SHOW: '#ed8936',
};

export function AdTracking() {
  const [preset, setPreset] = useState<RangePreset>('THIS_WEEK');
  const [customRange, setCustomRange] = useState(() => rangeFromPreset('THIS_WEEK'));
  const [locationId, setLocationId] = useState<string>('');
  const [campaignFilter, setCampaignFilter] = useState<string>('');
  const [slotHeatmapBarber, setSlotHeatmapBarber] = useState<string>('');
  const [slotDrillDown, setSlotDrillDown] = useState<{
    barberName: string;
    bookingDate: string;
    bookingTime: string;
    searches: number;
    eventDates: { eventDate: string; count: number }[];
  } | null>(null);

  const range = preset === 'CUSTOM' ? customRange : rangeFromPreset(preset);

  const params: Record<string, string> = { startDate: range.startDate, endDate: range.endDate };
  if (locationId) params.locationId = locationId;

  const { data, isLoading } = useQuery({
    queryKey: ['ad-tracking', params],
    queryFn: () => api.getAdTrackingReport(params),
  });

  const { data: locations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: () => api.getLocations(),
  });

  const totals = data?.totals;
  const campaigns = data?.campaigns || [];
  const recentBookings = data?.recentBookings || [];
  const googleAdsHeatmap: { dow: number; hour: number; bookings: number }[] =
    data?.googleAdsHeatmap || [];
  const googleAdsTotals = data?.googleAdsTotals as
    | {
        impressions: number;
        clicks: number;
        costAud: number;
        conversions: number;
        conversionValue: number;
        ctr: number;
        avgCpcAud: number;
        lastSyncedAt: string | null;
      }
    | undefined;

  // Google Ads conversion rate — uses Google Ads' own `conversions` count,
  // not our DB bookings count. This avoids the attribution-window mismatch
  // where a booking attributed via long-lived sessionStorage gclid would
  // inflate the numerator beyond the clicks that happened in this period.
  const googleAdsConversionRate = (() => {
    const clicks = googleAdsTotals?.clicks ?? 0;
    const conversions = googleAdsTotals?.conversions ?? 0;
    return clicks > 0 ? (conversions / clicks) * 100 : null;
  })();

  // Cost per acquisition (CPA) — Google Ads spend ÷ Google Ads conversions.
  // Same attribution source as Conv. Rate so the two numbers stay consistent.
  const googleAdsCpa = (() => {
    const cost = googleAdsTotals?.costAud ?? 0;
    const conversions = googleAdsTotals?.conversions ?? 0;
    return conversions > 0 ? cost / conversions : null;
  })();

  // GA4 data: sessions, funnel, top sources.
  const ga4Totals = data?.ga4Totals as
    | {
        sessions: number;
        engagedSessions: number;
        totalUsers: number;
        conversions: number;
        totalRevenue: number;
        googleAdsSessions: number;
        googleAdsEngagedSessions: number;
        googleAdsUsers: number;
        googleAdsConversions: number;
        engagementRate: number;
        lastSyncedAt: string | null;
      }
    | undefined;
  const ga4Funnel = (data?.ga4Funnel as { eventName: string; count: number }[] | undefined) ?? [];
  const ga4TopSources =
    (data?.ga4TopSources as
      | {
          source: string;
          medium: string;
          campaign: string;
          sessions: number;
          engagedSessions: number;
          totalUsers: number;
          conversions: number;
        }[]
      | undefined) ?? [];
  const ga4GoogleAdsEvents = (data?.ga4GoogleAdsEvents as Record<string, number> | undefined) ?? {};
  const reconciliation = data?.reconciliation as
    | { clipDate: string | null; googleAdsBookingsInSyncedRange: number | null }
    | undefined;
  const ga4TopDemand =
    (data?.ga4TopDemand as
      | { barberName: string; bookingDate: string; searches: number }[]
      | undefined) ?? [];
  const unmetDemand =
    (data?.unmetDemand as
      | {
          barberName: string;
          dateQueried: string;
          searches: number;
          firstSearch: string | null;
          lastSearch: string | null;
        }[]
      | undefined) ?? [];
  const unmetDemandDaily =
    (data?.unmetDemandDaily as
      | { date: string; searches: number; barbersImpacted: number }[]
      | undefined) ?? [];
  const barberSlotDemand =
    (data?.barberSlotDemand as
      | {
          barberName: string;
          bookingDate: string;
          bookingTime: string;
          searches: number;
          eventDates: { eventDate: string; count: number }[];
        }[]
      | undefined) ?? [];
  const availabilitySearchTimes =
    (data?.availabilitySearchTimes as Record<string, string[]> | undefined) ?? {};

  // Human-readable funnel step labels.
  const FUNNEL_LABELS: Record<string, string> = {
    location_selected: 'Location selected',
    booking_started: 'Booking started',
    service_added: 'Service added',
    service_removed: 'Service removed',
    barber_selected: 'Barber selected',
    date_selected: 'Date selected',
    time_selected: 'Time selected',
    booking_submitted: 'Booking submitted',
    booking_completed: 'Booking completed',
    purchase: 'Purchase (revenue event)',
    booking_viewed: 'Booking viewed',
    booking_failed: 'Booking failed',
  };

  // Funnel drop-off: only show the "main flow" events (drop -removed/-failed/-viewed).
  const mainFunnelSteps = [
    'booking_started',
    'service_added',
    'barber_selected',
    'date_selected',
    'time_selected',
    'booking_submitted',
    'booking_completed',
  ];
  const funnelDisplay = mainFunnelSteps.map((eventName) => {
    const step = ga4Funnel.find((f) => f.eventName === eventName);
    return { eventName, label: FUNNEL_LABELS[eventName], count: step?.count ?? 0 };
  });
  const funnelMax = funnelDisplay[0]?.count ?? 0;

  // GA4 vs Google Ads reconciliation — sessions from google/cpc vs clicks reported by Ads.
  const ga4AdsReconciliation = (() => {
    if (!ga4Totals || !googleAdsTotals) return null;
    const sessions = ga4Totals.googleAdsSessions;
    const clicks = googleAdsTotals.clicks;
    if (clicks === 0) return null;
    return {
      sessions,
      clicks,
      ratio: sessions / clicks, // >1 means GA4 sees more sessions than Ads reports clicks
    };
  })();

  // Build a 7x24 matrix (dow: 0=Sunday..6=Saturday, hour: 0..23) of Google Ads bookings
  const heatmapMatrix = useMemo(() => {
    const m: number[][] = Array.from({ length: 7 }, () => new Array(24).fill(0));
    for (const row of googleAdsHeatmap) {
      if (row.dow >= 0 && row.dow <= 6 && row.hour >= 0 && row.hour <= 23) {
        m[row.dow][row.hour] = row.bookings;
      }
    }
    return m;
  }, [googleAdsHeatmap]);

  const heatmapMax = useMemo(
    () => heatmapMatrix.reduce((mx, row) => Math.max(mx, ...row), 0),
    [heatmapMatrix],
  );

  // All-time campaign names for the filter dropdown (backend supplies this
  // list so it doesn't shrink when the user picks a narrow date range).
  // Falls back to in-period campaigns if the field isn't on the response yet.
  const allCampaignsServer: string[] | undefined = data?.allCampaigns;
  const uniqueCampaigns = useMemo(() => {
    if (allCampaignsServer && allCampaignsServer.length > 0) return allCampaignsServer;
    const names = new Set<string>();
    campaigns.forEach((c: any) => {
      if (c.campaign) names.add(c.campaign);
    });
    recentBookings.forEach((b: any) => {
      if (b.utm_campaign) names.add(b.utm_campaign);
    });
    return Array.from(names).sort();
  }, [allCampaignsServer, campaigns, recentBookings]);

  // Classify a recent booking row the same way the backend groups campaigns.
  const classifyBooking = (b: any): 'google_ads' | 'untracked' | 'other' => {
    if (b.gclid || (b.utm_source === 'google' && b.utm_medium === 'cpc')) return 'google_ads';
    if (!b.utm_source && !b.gclid) return 'untracked';
    return 'other';
  };

  // Filter by channel pseudo-options ("__google_ads__", "__untracked__") or exact campaign name.
  const filteredCampaigns =
    campaignFilter === '__google_ads__'
      ? campaigns.filter((c: any) => c.channel_type === 'google_ads')
      : campaignFilter === '__untracked__'
        ? campaigns.filter((c: any) => c.channel_type === 'untracked')
        : campaignFilter
          ? campaigns.filter((c: any) => c.campaign === campaignFilter)
          : campaigns;

  const filteredBookings =
    campaignFilter === '__google_ads__'
      ? recentBookings.filter((b: any) => classifyBooking(b) === 'google_ads')
      : campaignFilter === '__untracked__'
        ? recentBookings.filter((b: any) => classifyBooking(b) === 'untracked')
        : campaignFilter
          ? recentBookings.filter((b: any) => b.utm_campaign === campaignFilter)
          : recentBookings;

  // When the channel filter is active, re-derive the KPI numbers from
  // filteredCampaigns so the cards reflect the selected channel.
  // Each row in campaigns is already aggregated server-side by
  // (channel_type, source, medium, campaign), so summing gives correct totals.
  const filteredTotals = useMemo(() => {
    if (!campaignFilter) return null; // Use server totals unchanged.
    const sum = (key: string) =>
      filteredCampaigns.reduce((acc: number, r: any) => acc + Number(r[key] ?? 0), 0);
    const isGoogleAdsFilter = campaignFilter === '__google_ads__';
    const isUntrackedFilter = campaignFilter === '__untracked__';
    const filteredBookingsCount = sum('total_bookings');
    return {
      total_bookings: filteredBookingsCount,
      completed_bookings: sum('completed_bookings'),
      completed_revenue: sum('completed_revenue'),
      forecast_revenue: sum('forecast_revenue'),
      google_ads_bookings: isGoogleAdsFilter ? filteredBookingsCount : 0,
      tracked_bookings: isUntrackedFilter ? 0 : filteredBookingsCount,
      untracked_bookings: isUntrackedFilter ? filteredBookingsCount : 0,
    };
  }, [campaignFilter, filteredCampaigns]);

  // displayTotals is what the KPI row renders — filtered if a channel is
  // selected, otherwise the unmodified server aggregate.
  const displayTotals = filteredTotals ?? totals;

  // When the channel filter is "No tracking only", the Google Ads KPIs
  // (clicks/spend/conv rate/conversions) make no sense — suppress them.
  // When filter is "Google Ads only" or empty, show the full ad-side block.
  // When filter is a specific campaign, show ad-side KPIs (they still apply).
  const showGoogleAdsKpis = campaignFilter !== '__untracked__';

  const handlePreset = (p: RangePreset) => {
    setPreset(p);
    if (p !== 'CUSTOM') setCustomRange(rangeFromPreset(p));
  };

  const formatRangeDate = (d: string) => {
    const dt = new Date(`${d}T12:00:00`);
    return dt.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: BRISBANE_TZ,
    });
  };

  return (
    <div style={{ padding: 'var(--spacing-4)', maxWidth: 1200 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--spacing-4)',
          flexWrap: 'wrap',
          marginBottom: 'var(--spacing-4)',
        }}
      >
        <h1
          style={{
            fontFamily: 'var(--font-family-heading)',
            fontSize: 'var(--font-size-xl)',
          }}
        >
          Booking Analytics
        </h1>

        {/* Preset Picker */}
        <div style={{ display: 'grid', gap: 'var(--spacing-2)', justifyItems: 'end' }}>
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-2)',
              flexWrap: 'wrap',
              justifyContent: 'flex-end',
            }}
          >
            {PRESETS.map((item) => (
              <button
                key={item}
                onClick={() => handlePreset(item)}
                style={{
                  ...presetButtonStyle,
                  backgroundColor: preset === item ? 'var(--color-accent)' : 'var(--color-surface)',
                  borderColor: preset === item ? 'var(--color-accent)' : 'var(--color-border)',
                  color: preset === item ? '#1e1e1e' : 'var(--color-text)',
                }}
              >
                {PRESET_LABELS[item]}
              </button>
            ))}
          </div>
          {preset === 'CUSTOM' ? (
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
              <input
                type="date"
                value={customRange.startDate}
                onChange={(e) => setCustomRange({ ...customRange, startDate: e.target.value })}
                style={inputStyle}
              />
              <input
                type="date"
                value={customRange.endDate}
                onChange={(e) => setCustomRange({ ...customRange, endDate: e.target.value })}
                style={inputStyle}
              />
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
              {formatRangeDate(range.startDate)} – {formatRangeDate(range.endDate)}
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div
        style={{
          marginBottom: 'var(--spacing-4)',
          display: 'flex',
          gap: 'var(--spacing-2)',
          flexWrap: 'wrap',
        }}
      >
        <select
          value={locationId}
          onChange={(e) => setLocationId(e.target.value)}
          style={inputStyle}
        >
          <option value="">All Locations</option>
          {locations.map((l: any) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <select
          value={campaignFilter}
          onChange={(e) => setCampaignFilter(e.target.value)}
          style={inputStyle}
        >
          <option value="">All Channels</option>
          <option value="__google_ads__">Google Ads only</option>
          <option value="__untracked__">No tracking only</option>
          {uniqueCampaigns.length > 0 && <option disabled>──────────</option>}
          {uniqueCampaigns.map((c) => (
            <option key={c} value={c}>
              {c}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      ) : !totals || totals.total_bookings === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', padding: 'var(--spacing-6)' }}>
          <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-md)' }}>
            No ad-attributed bookings found for this period.
          </p>
          <p
            style={{
              color: 'var(--color-text-muted)',
              fontSize: 'var(--font-size-sm)',
              marginTop: 'var(--spacing-2)',
            }}
          >
            Bookings will appear here when customers arrive from Google Ads (via gclid or UTM
            parameters).
          </p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-3)',
              flexWrap: 'wrap',
              marginBottom: 'var(--spacing-4)',
            }}
          >
            <div style={kpiCardStyle}>
              <p style={{ ...labelStyle, textAlign: 'center' }}>Total Bookings</p>
              <p
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                  color: 'var(--color-text)',
                }}
                title="All customer-facing bookings in this period (source = 'customer'), tracked or not"
              >
                {displayTotals.total_bookings}
              </p>
            </div>
            <div style={kpiCardStyle}>
              <p style={{ ...labelStyle, textAlign: 'center' }}>Google Ads</p>
              <p
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                  color: 'var(--color-accent)',
                }}
                title="Bookings attributed to Google Ads clicks (gclid present or utm_source=google + utm_medium=cpc)"
              >
                {displayTotals.google_ads_bookings ?? displayTotals.unique_ad_clicks}
              </p>
            </div>
            <div style={kpiCardStyle}>
              <p style={{ ...labelStyle, textAlign: 'center' }}>Booking with tracking</p>
              <p
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                  color: 'var(--color-text)',
                }}
                title="Customer bookings with a tracked source (Google Ads + website + SMS + GBP)"
              >
                {displayTotals.tracked_bookings ?? displayTotals.total_bookings}
              </p>
            </div>
            <div style={kpiCardStyle}>
              <p style={{ ...labelStyle, textAlign: 'center' }}>Completed</p>
              <p
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                  color: '#48bb78',
                }}
              >
                {displayTotals.completed_bookings}
              </p>
            </div>
            <div style={kpiCardStyle}>
              <p style={{ ...labelStyle, textAlign: 'center' }}>Revenue</p>
              <p
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                  color: '#48bb78',
                }}
              >
                $
                {Number(displayTotals.completed_revenue).toLocaleString('en-AU', {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>
            <div style={kpiCardStyle}>
              <p style={{ ...labelStyle, textAlign: 'center' }}>Forecast Revenue</p>
              <p
                style={{
                  fontSize: 'var(--font-size-xl)',
                  fontFamily: 'var(--font-family-heading)',
                  fontWeight: 700,
                  color: 'var(--color-accent)',
                }}
                title="Expected revenue from BOOKED appointments that haven't happened yet"
              >
                $
                {Number(displayTotals.forecast_revenue ?? 0).toLocaleString('en-AU', {
                  minimumFractionDigits: 2,
                })}
              </p>
            </div>
            {googleAdsTotals && showGoogleAdsKpis && (
              <>
                <div style={kpiCardStyle}>
                  <p style={{ ...labelStyle, textAlign: 'center' }}>Ad Clicks</p>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xl)',
                      fontFamily: 'var(--font-family-heading)',
                      fontWeight: 700,
                      color: 'var(--color-text)',
                    }}
                    title="Total Google Ads clicks in this period (source: google_ads_campaign_daily)"
                  >
                    {googleAdsTotals.clicks.toLocaleString('en-AU')}
                  </p>
                </div>
                <div style={kpiCardStyle}>
                  <p style={{ ...labelStyle, textAlign: 'center' }}>Ad Spend</p>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xl)',
                      fontFamily: 'var(--font-family-heading)',
                      fontWeight: 700,
                      color: 'var(--color-text)',
                    }}
                    title="Google Ads cost in AUD"
                  >
                    $
                    {googleAdsTotals.costAud.toLocaleString('en-AU', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </p>
                </div>
                <div style={kpiCardStyle}>
                  <p style={{ ...labelStyle, textAlign: 'center' }}>Ad Conversions</p>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xl)',
                      fontFamily: 'var(--font-family-heading)',
                      fontWeight: 700,
                      color: 'var(--color-text)',
                    }}
                    title="Conversions as reported by Google Ads (uses Google's own attribution window)"
                  >
                    {googleAdsTotals.conversions.toLocaleString('en-AU', {
                      maximumFractionDigits: 1,
                    })}
                  </p>
                </div>
                <div style={kpiCardStyle}>
                  <p style={{ ...labelStyle, textAlign: 'center' }}>Conv. Rate</p>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xl)',
                      fontFamily: 'var(--font-family-heading)',
                      fontWeight: 700,
                      color: 'var(--color-accent)',
                    }}
                    title="Google Ads conversions ÷ Google Ads clicks × 100. Uses Google's own attribution to avoid the sessionStorage-gclid over-count."
                  >
                    {googleAdsConversionRate == null
                      ? '—'
                      : `${googleAdsConversionRate.toFixed(1)}%`}
                  </p>
                </div>
                <div style={kpiCardStyle}>
                  <p style={{ ...labelStyle, textAlign: 'center' }}>CPA</p>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xl)',
                      fontFamily: 'var(--font-family-heading)',
                      fontWeight: 700,
                      color: 'var(--color-accent)',
                    }}
                    title="Cost per acquisition: Google Ads spend ÷ Google Ads conversions (Google's own attribution)."
                  >
                    {googleAdsCpa == null
                      ? '—'
                      : `$${googleAdsCpa.toLocaleString('en-AU', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* GA4 section — Traffic & Funnel. Only shown when GA4 data is present. */}
          {ga4Totals && ga4Totals.sessions > 0 && (
            <>
              {/* GA4 overview strip */}
              <div
                style={{
                  ...cardStyle,
                  marginBottom: 'var(--spacing-4)',
                  background: 'var(--color-surface-elevated, var(--color-surface))',
                }}
              >
                <h2
                  style={{
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: 'var(--font-size-md)',
                    marginBottom: 'var(--spacing-3)',
                  }}
                >
                  Traffic (GA4)
                </h2>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 'var(--spacing-3)',
                  }}
                >
                  <div>
                    <p style={{ ...labelStyle }}>Sessions</p>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xl)',
                        fontFamily: 'var(--font-family-heading)',
                        fontWeight: 700,
                      }}
                    >
                      {ga4Totals.sessions.toLocaleString('en-AU')}
                    </p>
                  </div>
                  <div>
                    <p style={{ ...labelStyle }}>Users</p>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xl)',
                        fontFamily: 'var(--font-family-heading)',
                        fontWeight: 700,
                      }}
                    >
                      {ga4Totals.totalUsers.toLocaleString('en-AU')}
                    </p>
                  </div>
                  <div>
                    <p style={{ ...labelStyle }}>Engaged</p>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xl)',
                        fontFamily: 'var(--font-family-heading)',
                        fontWeight: 700,
                      }}
                    >
                      {(ga4Totals.engagementRate * 100).toFixed(1)}%
                    </p>
                  </div>
                  {ga4AdsReconciliation && (
                    <div
                      title={`GA4 sessions from google/cpc vs Google Ads' reported clicks. Ratio ≈1.0 means tagging is healthy; big drift indicates a broken gclid chain.`}
                    >
                      <p style={{ ...labelStyle }}>GA4 vs Ads</p>
                      <p
                        style={{
                          fontSize: 'var(--font-size-xl)',
                          fontFamily: 'var(--font-family-heading)',
                          fontWeight: 700,
                          color:
                            Math.abs(ga4AdsReconciliation.ratio - 1) > 0.5
                              ? 'var(--color-error)'
                              : 'var(--color-text)',
                        }}
                      >
                        {ga4AdsReconciliation.sessions}:{ga4AdsReconciliation.clicks}
                      </p>
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--color-text-muted)',
                        }}
                      >
                        {ga4AdsReconciliation.ratio.toFixed(2)}× sessions/clicks
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Reconciliation — Ads vs GA4 vs DB for google/cpc only.
                   Reveals tagging / attribution health at a glance. */}
              {googleAdsTotals && googleAdsTotals.clicks > 0 && (
                <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
                  <h2
                    style={{
                      fontFamily: 'var(--font-family-heading)',
                      fontSize: 'var(--font-size-md)',
                      marginBottom: 'var(--spacing-1)',
                    }}
                  >
                    Google Ads Reconciliation
                  </h2>
                  <p
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      marginBottom: 'var(--spacing-3)',
                    }}
                  >
                    Cross-check the same Google Ads traffic across three systems. Sessions ≥ Clicks
                    is healthy; bookings across Ads / GA4 / DB should agree within ~20%.
                    {reconciliation?.clipDate && (
                      <>
                        {' '}
                        <strong>
                          Compared through{' '}
                          {new Date(`${reconciliation.clipDate}T12:00:00`).toLocaleDateString(
                            'en-AU',
                            { day: 'numeric', month: 'short', timeZone: BRISBANE_TZ },
                          )}
                        </strong>{' '}
                        (Google Ads + GA4 are 24h-lagged — today's data isn't in yet, so DB counts
                        are clipped to match).
                      </>
                    )}
                  </p>
                  {(() => {
                    const clicks = googleAdsTotals.clicks;
                    const adsConversions = googleAdsTotals.conversions;
                    const ga4Sessions = ga4Totals?.googleAdsSessions ?? 0;
                    const ga4BookingCompleted = ga4GoogleAdsEvents.booking_completed ?? 0;
                    // Use the clipped count when available so we compare the same window.
                    const dbBookings =
                      reconciliation?.googleAdsBookingsInSyncedRange ??
                      totals?.google_ads_bookings ??
                      0;
                    const sessionsRatio = clicks > 0 ? ga4Sessions / clicks : 0;
                    const sessionsHealthy =
                      sessionsRatio >= 0.7 && sessionsRatio <= 1.8 && ga4Sessions > 0;
                    // For booking reconciliation, compare the three "how many bookings" signals.
                    const bookingNumbers = [adsConversions, ga4BookingCompleted, dbBookings].filter(
                      (n) => n > 0,
                    );
                    const maxB = Math.max(...bookingNumbers, 0);
                    const minB = Math.min(...bookingNumbers, Infinity);
                    const bookingsHealthy =
                      maxB > 0 && (maxB - minB) / maxB <= 0.4 && bookingNumbers.length >= 2;

                    const rows = [
                      {
                        label: 'Clicks / Sessions',
                        ads: clicks.toLocaleString('en-AU'),
                        ga4: ga4Sessions.toLocaleString('en-AU'),
                        db: '—',
                        note: sessionsHealthy
                          ? `${sessionsRatio.toFixed(2)}× — healthy`
                          : ga4Sessions === 0
                            ? 'No GA4 data yet'
                            : `${sessionsRatio.toFixed(2)}× — check gclid auto-tagging`,
                        healthy: sessionsHealthy,
                      },
                      {
                        label: 'Bookings completed',
                        ads: adsConversions.toLocaleString('en-AU', { maximumFractionDigits: 1 }),
                        ga4: ga4BookingCompleted.toLocaleString('en-AU'),
                        db: dbBookings.toLocaleString('en-AU'),
                        note: bookingsHealthy
                          ? 'Three sources agree — loop is closed'
                          : bookingNumbers.length < 2
                            ? 'Not enough data yet'
                            : 'Drift >40% — investigate attribution',
                        healthy: bookingsHealthy,
                      },
                    ];

                    return (
                      <div style={{ overflowX: 'auto' }}>
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: 'var(--font-size-sm)',
                          }}
                        >
                          <thead>
                            <tr>
                              <th style={thStyle}>Metric</th>
                              <th style={{ ...thStyle, textAlign: 'right' }}>Google Ads</th>
                              <th style={{ ...thStyle, textAlign: 'right' }}>GA4 (google/cpc)</th>
                              <th style={{ ...thStyle, textAlign: 'right' }}>Your DB</th>
                              <th style={thStyle}>Health</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rows.map((r) => (
                              <tr key={r.label}>
                                <td style={tdStyle}>{r.label}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{r.ads}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{r.ga4}</td>
                                <td style={{ ...tdStyle, textAlign: 'right' }}>{r.db}</td>
                                <td
                                  style={{
                                    ...tdStyle,
                                    color: r.healthy ? '#48bb78' : 'var(--color-error)',
                                    fontWeight: 600,
                                  }}
                                >
                                  {r.healthy ? '✓ ' : '⚠ '}
                                  {r.note}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Funnel */}
              <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
                <h2
                  style={{
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: 'var(--font-size-md)',
                    marginBottom: 'var(--spacing-1)',
                  }}
                >
                  Booking Funnel (GA4)
                </h2>
                <p
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-muted)',
                    marginBottom: 'var(--spacing-3)',
                  }}
                >
                  Event counts — each bar is relative to "Booking started". Drop-off between steps
                  is where you lose the customer.
                </p>
                {funnelMax === 0 ? (
                  <p style={{ color: 'var(--color-text-muted)' }}>
                    No funnel events in this period.
                  </p>
                ) : (
                  <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                    {funnelDisplay.map((step, idx) => {
                      const pct = funnelMax > 0 ? step.count / funnelMax : 0;
                      const prev = idx > 0 ? funnelDisplay[idx - 1].count : 0;
                      const stepDrop = prev > 0 ? ((prev - step.count) / prev) * 100 : null;
                      return (
                        <div
                          key={step.eventName}
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '180px 1fr 80px',
                            alignItems: 'center',
                            gap: 'var(--spacing-3)',
                            fontSize: 'var(--font-size-sm)',
                          }}
                        >
                          <span
                            style={{
                              color:
                                idx === funnelDisplay.length - 1
                                  ? 'var(--color-text)'
                                  : 'var(--color-text-muted)',
                              fontWeight: idx === funnelDisplay.length - 1 ? 600 : 400,
                            }}
                          >
                            {step.label}
                          </span>
                          <div
                            style={{
                              height: 22,
                              borderRadius: 4,
                              background: 'var(--color-border-light, rgba(0,0,0,0.05))',
                              position: 'relative',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                width: `${Math.max(2, pct * 100)}%`,
                                background:
                                  idx === funnelDisplay.length - 1
                                    ? '#48bb78'
                                    : 'var(--color-accent)',
                                borderRadius: 4,
                                transition: 'width 0.3s',
                              }}
                            />
                          </div>
                          <span
                            style={{
                              textAlign: 'right',
                              fontFamily: 'var(--font-family-heading)',
                              fontWeight: 600,
                            }}
                            title={
                              stepDrop != null && stepDrop > 0
                                ? `Dropped ${stepDrop.toFixed(0)}% from previous step`
                                : undefined
                            }
                          >
                            {step.count.toLocaleString('en-AU')}
                            {stepDrop != null && stepDrop > 0 && (
                              <span
                                style={{
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--color-text-muted)',
                                  display: 'block',
                                  fontWeight: 400,
                                }}
                              >
                                −{stepDrop.toFixed(0)}%
                              </span>
                            )}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Top sources */}
              <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
                <h2
                  style={{
                    fontFamily: 'var(--font-family-heading)',
                    fontSize: 'var(--font-size-md)',
                    marginBottom: 'var(--spacing-3)',
                  }}
                >
                  Top Traffic Sources (GA4)
                </h2>
                <ResponsiveList
                  rows={ga4TopSources}
                  getRowKey={(r: any, i: number) => `${r.source}-${r.medium}-${r.campaign}-${i}`}
                  columns={[
                    {
                      key: 'label',
                      label: 'Source',
                      primaryOnMobile: true,
                      render: (r: any) =>
                        `${r.source} / ${r.medium}${r.campaign && r.campaign !== '(not set)' ? ` · ${r.campaign}` : ''}`,
                    },
                    {
                      key: 'source',
                      label: 'Source',
                      render: (r: any) => r.source,
                      hideOnMobile: true,
                    },
                    {
                      key: 'medium',
                      label: 'Medium',
                      render: (r: any) => r.medium,
                      hideOnMobile: true,
                    },
                    {
                      key: 'campaign',
                      label: 'Campaign',
                      render: (r: any) => r.campaign,
                      hideOnMobile: true,
                    },
                    {
                      key: 'sessions',
                      label: 'Sessions',
                      align: 'right',
                      render: (r: any) => r.sessions.toLocaleString('en-AU'),
                    },
                    {
                      key: 'users',
                      label: 'Users',
                      align: 'right',
                      render: (r: any) => r.totalUsers.toLocaleString('en-AU'),
                    },
                    {
                      key: 'conversions',
                      label: 'Conversions',
                      align: 'right',
                      render: (r: any) =>
                        r.conversions.toLocaleString('en-AU', { maximumFractionDigits: 1 }),
                    },
                  ]}
                  emptyMessage="No traffic recorded for this period."
                />
              </div>
            </>
          )}

          {/* Searched Barber × Date (from GA4 custom dimensions) */}
          <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-family-heading)',
                fontSize: 'var(--font-size-md)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              Searched Barber × Date
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              Which (barber, looked-at date) combinations customers picked most — from GA4
              date_selected + time_selected events. Only counts events fired after the three custom
              dimensions (barber_name, booking_date, booking_time) were registered in GA4 Admin.
            </p>
            <ResponsiveList
              rows={ga4TopDemand}
              getRowKey={(r: any, i: number) => `${r.barberName}-${r.bookingDate}-${i}`}
              columns={[
                {
                  key: 'combo',
                  label: 'Barber · Date',
                  primaryOnMobile: true,
                  render: (r: any) => `${r.barberName} · ${r.bookingDate}`,
                },
                {
                  key: 'barber',
                  label: 'Barber',
                  render: (r: any) => r.barberName,
                  hideOnMobile: true,
                },
                {
                  key: 'date',
                  label: 'Date looked at',
                  render: (r: any) => r.bookingDate,
                  hideOnMobile: true,
                },
                {
                  key: 'searches',
                  label: 'Searches',
                  align: 'right',
                  render: (r: any) => r.searches.toLocaleString('en-AU'),
                },
              ]}
              emptyMessage="No searches recorded yet. GA4 custom dimensions were registered today, so data starts appearing after tomorrow's 5:30am sync."
            />
          </div>

          {/* Unmet Demand Trend — line chart over time */}
          {unmetDemandDaily.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
              <h2
                style={{
                  fontFamily: 'var(--font-family-heading)',
                  fontSize: 'var(--font-size-md)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                Unmet Demand Trend
              </h2>
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                Zero-slot searches per day — rising trend = demand outgrowing supply.
              </p>
              {(() => {
                const W = 800;
                const H = 220;
                const PAD_L = 44;
                const PAD_R = 12;
                const PAD_T = 14;
                const PAD_B = 34;
                const plotW = W - PAD_L - PAD_R;
                const plotH = H - PAD_T - PAD_B;
                const maxY = Math.max(1, ...unmetDemandDaily.map((d) => d.searches));
                const niceMax = Math.ceil(maxY / 5) * 5 || 5;
                const n = unmetDemandDaily.length;
                const xFor = (i: number) => PAD_L + (n === 1 ? plotW / 2 : (plotW * i) / (n - 1));
                const yFor = (v: number) => PAD_T + plotH - (plotH * v) / niceMax;

                const pathD = unmetDemandDaily
                  .map(
                    (d, i) =>
                      `${i === 0 ? 'M' : 'L'}${xFor(i).toFixed(2)},${yFor(d.searches).toFixed(2)}`,
                  )
                  .join(' ');

                // Area fill path: line + close down to the x-axis baseline.
                const areaD = `${pathD} L${xFor(n - 1).toFixed(2)},${(PAD_T + plotH).toFixed(2)} L${xFor(0).toFixed(2)},${(PAD_T + plotH).toFixed(2)} Z`;

                // 5 Y-axis grid ticks.
                const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(niceMax * f));

                // X-axis labels — show every ~N/6 day to avoid clutter.
                const xLabelStep = Math.max(1, Math.ceil(n / 7));

                return (
                  <div style={{ overflowX: 'auto' }}>
                    <svg
                      viewBox={`0 0 ${W} ${H}`}
                      preserveAspectRatio="none"
                      style={{ width: '100%', height: 'auto', minWidth: 480, display: 'block' }}
                    >
                      {/* Y grid lines + labels */}
                      {yTicks.map((t) => (
                        <g key={t}>
                          <line
                            x1={PAD_L}
                            x2={W - PAD_R}
                            y1={yFor(t)}
                            y2={yFor(t)}
                            stroke="var(--color-border-light, rgba(0,0,0,0.08))"
                            strokeWidth={1}
                          />
                          <text
                            x={PAD_L - 6}
                            y={yFor(t) + 4}
                            fontSize="11"
                            fill="var(--color-text-muted)"
                            textAnchor="end"
                          >
                            {t}
                          </text>
                        </g>
                      ))}

                      {/* X-axis baseline */}
                      <line
                        x1={PAD_L}
                        x2={W - PAD_R}
                        y1={PAD_T + plotH}
                        y2={PAD_T + plotH}
                        stroke="var(--color-border)"
                        strokeWidth={1}
                      />

                      {/* Area fill */}
                      <path d={areaD} fill="var(--color-error)" fillOpacity={0.08} />

                      {/* Line */}
                      <path
                        d={pathD}
                        fill="none"
                        stroke="var(--color-error)"
                        strokeWidth={2.25}
                        strokeLinejoin="round"
                        strokeLinecap="round"
                      />

                      {/* Data points */}
                      {unmetDemandDaily.map((d, i) => (
                        <g key={d.date}>
                          <circle
                            cx={xFor(i)}
                            cy={yFor(d.searches)}
                            r={3}
                            fill="var(--color-error)"
                          />
                          <title>{`${d.date}: ${d.searches} zero-slot search${d.searches === 1 ? '' : 'es'}${d.barbersImpacted ? ` (${d.barbersImpacted} barber${d.barbersImpacted === 1 ? '' : 's'})` : ''}`}</title>
                        </g>
                      ))}

                      {/* X labels */}
                      {unmetDemandDaily.map((d, i) =>
                        i % xLabelStep === 0 || i === n - 1 ? (
                          <text
                            key={d.date}
                            x={xFor(i)}
                            y={H - 10}
                            fontSize="10"
                            fill="var(--color-text-muted)"
                            textAnchor="middle"
                          >
                            {new Date(`${d.date}T12:00:00`).toLocaleDateString('en-AU', {
                              day: 'numeric',
                              month: 'short',
                              timeZone: BRISBANE_TZ,
                            })}
                          </text>
                        ) : null,
                      )}
                    </svg>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--color-text-muted)',
                        marginTop: 'var(--spacing-1)',
                      }}
                    >
                      <span>
                        Total over period:{' '}
                        <strong style={{ color: 'var(--color-error)' }}>
                          {unmetDemandDaily
                            .reduce((sum, d) => sum + d.searches, 0)
                            .toLocaleString('en-AU')}
                        </strong>{' '}
                        zero-slot searches
                      </span>
                      <span>
                        Peak day:{' '}
                        <strong>
                          {
                            unmetDemandDaily.reduce(
                              (best, d) => (d.searches > best.searches ? d : best),
                              unmetDemandDaily[0],
                            ).date
                          }
                        </strong>
                      </span>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Unmet Demand (from server-side availability_query_log) */}
          <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-family-heading)',
                fontSize: 'var(--font-size-md)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              Unmet Demand
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              (Barber, date) combinations customers searched but saw zero available slots. Recorded
              server-side on every /availability call — catches the "looked-but-couldn't-book" case
              that GA4 can't see. Signal for capacity planning: high-search zero-slot rows are the
              slots you're losing bookings to.
            </p>
            <ResponsiveList
              rows={unmetDemand}
              getRowKey={(r: any, i: number) => `${r.barberName}-${r.dateQueried}-${i}`}
              columns={[
                {
                  key: 'combo',
                  label: 'Barber · Date',
                  primaryOnMobile: true,
                  render: (r: any) => `${r.barberName} · ${r.dateQueried}`,
                },
                {
                  key: 'barber',
                  label: 'Barber',
                  render: (r: any) => r.barberName,
                  hideOnMobile: true,
                },
                {
                  key: 'date',
                  label: 'Date sought',
                  render: (r: any) => r.dateQueried,
                  hideOnMobile: true,
                },
                {
                  key: 'searches',
                  label: 'Zero-slot searches',
                  align: 'right',
                  render: (r: any) => (
                    <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>
                      {r.searches.toLocaleString('en-AU')}
                    </span>
                  ),
                },
                {
                  key: 'last',
                  label: 'Most recent',
                  render: (r: any) =>
                    r.lastSearch
                      ? new Date(r.lastSearch).toLocaleString('en-AU', {
                          timeZone: BRISBANE_TZ,
                          day: 'numeric',
                          month: 'short',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : '—',
                  hideOnMobile: true,
                },
              ]}
              emptyMessage="No zero-slot searches recorded. Either availability is healthy, or no customers have browsed the booking flow yet today."
            />
          </div>

          {/* Slot Demand Heatmap (per barber) */}
          {barberSlotDemand.length > 0 && (
            <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
              <h2
                style={{
                  fontFamily: 'var(--font-family-heading)',
                  fontSize: 'var(--font-size-md)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                Slot Demand Heatmap
              </h2>
              <p
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-muted)',
                  marginBottom: 'var(--spacing-3)',
                }}
              >
                Which (barber · booking date · time slot) combinations customers searched most, from
                GA4 <code>time_selected</code> events. Darker cells = more searches for that exact
                slot. Click any cell to see when those searches actually happened — tells you
                whether demand is last-minute or planned days in advance.
              </p>
              {(() => {
                // Pick default barber if not set (most-searched).
                const barbers = Array.from(
                  new Set(barberSlotDemand.map((d) => d.barberName)),
                ).sort();
                const activeBarber = slotHeatmapBarber || barbers[0] || '';
                const forBarber = barberSlotDemand.filter((d) => d.barberName === activeBarber);
                const dates = Array.from(new Set(forBarber.map((d) => d.bookingDate))).sort();
                const times = Array.from(new Set(forBarber.map((d) => d.bookingTime))).sort();
                const grid = new Map<string, (typeof forBarber)[number]>();
                for (const row of forBarber) {
                  grid.set(`${row.bookingDate}|${row.bookingTime}`, row);
                }
                const maxSearches = Math.max(1, ...forBarber.map((d) => d.searches));
                const colourFor = (n: number): string => {
                  if (n === 0) return 'transparent';
                  const intensity = Math.min(1, n / maxSearches);
                  // Red scale — matches the Unmet Demand palette.
                  const alpha = 0.12 + intensity * 0.78;
                  return `rgba(220, 38, 38, ${alpha})`;
                };
                return (
                  <>
                    {/* Barber selector tabs */}
                    <div
                      style={{
                        display: 'flex',
                        gap: 'var(--spacing-1)',
                        marginBottom: 'var(--spacing-3)',
                        flexWrap: 'wrap',
                      }}
                    >
                      {barbers.map((b) => {
                        const total = barberSlotDemand
                          .filter((d) => d.barberName === b)
                          .reduce((sum, d) => sum + d.searches, 0);
                        const isActive = b === activeBarber;
                        return (
                          <button
                            key={b}
                            type="button"
                            onClick={() => {
                              setSlotHeatmapBarber(b);
                              setSlotDrillDown(null);
                            }}
                            style={{
                              padding: '4px 10px',
                              fontSize: 'var(--font-size-xs)',
                              borderRadius: 'var(--radius-md)',
                              border: '1px solid var(--color-border)',
                              background: isActive ? 'var(--color-text)' : 'var(--color-surface)',
                              color: isActive ? 'var(--color-surface)' : 'var(--color-text)',
                              cursor: 'pointer',
                              fontWeight: isActive ? 600 : 400,
                            }}
                          >
                            {b} <span style={{ opacity: 0.6 }}>({total})</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Grid */}
                    <div style={{ overflowX: 'auto' }}>
                      <table
                        style={{
                          borderCollapse: 'separate',
                          borderSpacing: 2,
                          fontSize: 'var(--font-size-xs)',
                        }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                position: 'sticky',
                                left: 0,
                                background: 'var(--color-surface)',
                                padding: '4px 8px',
                                textAlign: 'left',
                                fontWeight: 600,
                                color: 'var(--color-text-muted)',
                              }}
                            >
                              Date ↓ / Time →
                            </th>
                            {times.map((t) => (
                              <th
                                key={t}
                                style={{
                                  padding: '4px 6px',
                                  fontWeight: 500,
                                  color: 'var(--color-text-muted)',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {t}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {dates.map((d) => (
                            <tr key={d}>
                              <td
                                style={{
                                  position: 'sticky',
                                  left: 0,
                                  background: 'var(--color-surface)',
                                  padding: '4px 8px',
                                  fontWeight: 500,
                                  whiteSpace: 'nowrap',
                                  borderRight: '1px solid var(--color-border)',
                                }}
                              >
                                {d}
                              </td>
                              {times.map((t) => {
                                const cell = grid.get(`${d}|${t}`);
                                const n = cell?.searches ?? 0;
                                const isSelected =
                                  slotDrillDown?.barberName === activeBarber &&
                                  slotDrillDown?.bookingDate === d &&
                                  slotDrillDown?.bookingTime === t;
                                return (
                                  <td
                                    key={t}
                                    onClick={() => cell && setSlotDrillDown(cell)}
                                    title={
                                      n > 0
                                        ? `${activeBarber} · ${d} · ${t} — ${n} search${n === 1 ? '' : 'es'}`
                                        : ''
                                    }
                                    style={{
                                      width: 38,
                                      height: 28,
                                      textAlign: 'center',
                                      backgroundColor: colourFor(n),
                                      border: isSelected
                                        ? '2px solid var(--color-text)'
                                        : n > 0
                                          ? '1px solid rgba(220, 38, 38, 0.2)'
                                          : '1px solid var(--color-border)',
                                      cursor: n > 0 ? 'pointer' : 'default',
                                      color: n / maxSearches > 0.5 ? '#fff' : 'var(--color-text)',
                                      fontWeight: n > 0 ? 600 : 400,
                                      borderRadius: 'var(--radius-sm)',
                                    }}
                                  >
                                    {n > 0 ? n : ''}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {/* Drill-down panel */}
                    {slotDrillDown && slotDrillDown.barberName === activeBarber && (
                      <div
                        style={{
                          marginTop: 'var(--spacing-3)',
                          padding: 'var(--spacing-3)',
                          background: 'var(--color-background)',
                          border: '1px solid var(--color-border)',
                          borderRadius: 'var(--radius-md)',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'baseline',
                            marginBottom: 'var(--spacing-2)',
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                              {slotDrillDown.barberName} · {slotDrillDown.bookingDate} ·{' '}
                              {slotDrillDown.bookingTime}
                            </div>
                            <div
                              style={{
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-muted)',
                              }}
                            >
                              {slotDrillDown.searches} total search
                              {slotDrillDown.searches === 1 ? '' : 'es'} — spread across{' '}
                              {slotDrillDown.eventDates.length} day
                              {slotDrillDown.eventDates.length === 1 ? '' : 's'}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSlotDrillDown(null)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'var(--color-text-muted)',
                              cursor: 'pointer',
                              fontSize: 'var(--font-size-lg)',
                              lineHeight: 1,
                            }}
                            aria-label="Close"
                          >
                            ×
                          </button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                          {slotDrillDown.eventDates.map((ev) => {
                            const bookingDay = new Date(`${slotDrillDown.bookingDate}T00:00:00`);
                            const eventDay = new Date(`${ev.eventDate}T00:00:00`);
                            const leadDays = Math.round(
                              (bookingDay.getTime() - eventDay.getTime()) / (1000 * 60 * 60 * 24),
                            );
                            const leadLabel =
                              leadDays === 0
                                ? 'same day'
                                : leadDays === 1
                                  ? '1 day ahead'
                                  : leadDays > 1
                                    ? `${leadDays} days ahead`
                                    : `${Math.abs(leadDays)} day${Math.abs(leadDays) === 1 ? '' : 's'} after (re-check)`;
                            return (
                              <div
                                key={ev.eventDate}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  fontSize: 'var(--font-size-xs)',
                                  padding: '2px 0',
                                }}
                              >
                                <span>
                                  Searched on <strong>{ev.eventDate}</strong>{' '}
                                  <span style={{ color: 'var(--color-text-muted)' }}>
                                    ({leadLabel})
                                  </span>
                                </span>
                                <span style={{ fontWeight: 600 }}>
                                  {ev.count} event{ev.count === 1 ? '' : 's'}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                        {(() => {
                          const key = `${slotDrillDown.barberName}|${slotDrillDown.bookingDate}`;
                          const timestamps = availabilitySearchTimes[key] ?? [];
                          if (timestamps.length === 0) return null;
                          // Group timestamps by calendar day (Brisbane) for readability.
                          const byDay = new Map<string, string[]>();
                          for (const iso of timestamps) {
                            const d = new Date(iso);
                            const dayKey = new Intl.DateTimeFormat('en-CA', {
                              timeZone: BRISBANE_TZ,
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            }).format(d);
                            const timeStr = d.toLocaleTimeString('en-AU', {
                              timeZone: BRISBANE_TZ,
                              hour: 'numeric',
                              minute: '2-digit',
                              hour12: true,
                            });
                            const arr = byDay.get(dayKey) ?? [];
                            arr.push(timeStr);
                            byDay.set(dayKey, arr);
                          }
                          return (
                            <div
                              style={{
                                marginTop: 'var(--spacing-3)',
                                paddingTop: 'var(--spacing-2)',
                                borderTop: '1px dashed var(--color-border)',
                              }}
                            >
                              <div
                                style={{
                                  fontSize: 'var(--font-size-xs)',
                                  color: 'var(--color-text-muted)',
                                  marginBottom: 4,
                                }}
                              >
                                Clock times of every availability search for{' '}
                                <strong>{slotDrillDown.barberName}</strong> on{' '}
                                <strong>{slotDrillDown.bookingDate}</strong> (server-logged — covers
                                all time slots, not just {slotDrillDown.bookingTime}):
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  gap: 2,
                                  fontSize: 'var(--font-size-xs)',
                                }}
                              >
                                {Array.from(byDay.entries())
                                  .sort((a, b) => b[0].localeCompare(a[0]))
                                  .map(([day, times]) => (
                                    <div key={day} style={{ display: 'flex', gap: 8 }}>
                                      <span
                                        style={{
                                          fontWeight: 600,
                                          minWidth: 92,
                                          color: 'var(--color-text-muted)',
                                        }}
                                      >
                                        {day}
                                      </span>
                                      <span style={{ flex: 1 }}>{times.join(', ')}</span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {/* Campaigns Table */}
          <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-family-heading)',
                fontSize: 'var(--font-size-md)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              Campaign Breakdown
            </h2>
            <ResponsiveList
              rows={filteredCampaigns}
              getRowKey={(c: any, i: number) =>
                `${c.channel_type ?? ''}-${c.source ?? ''}-${c.medium ?? ''}-${c.campaign ?? ''}-${i}`
              }
              columns={[
                {
                  key: 'campaign',
                  label: 'Campaign',
                  primaryOnMobile: true,
                  render: (c: any) => {
                    const src = c.source || '—';
                    const med = c.medium ? ` / ${c.medium}` : '';
                    const cmp = c.campaign ? ` · ${c.campaign}` : '';
                    return `${src}${med}${cmp}`;
                  },
                },
                {
                  key: 'source',
                  label: 'Source',
                  render: (c: any) => c.source || '—',
                  hideOnMobile: true,
                },
                {
                  key: 'medium',
                  label: 'Medium',
                  render: (c: any) => c.medium || '—',
                  hideOnMobile: true,
                },
                {
                  key: 'campaignName',
                  label: 'Campaign',
                  render: (c: any) => c.campaign || '—',
                  hideOnMobile: true,
                },
                {
                  key: 'total_bookings',
                  label: 'Bookings',
                  align: 'right',
                  render: (c: any) => c.total_bookings,
                },
                {
                  key: 'completed_bookings',
                  label: 'Completed',
                  align: 'right',
                  render: (c: any) => (
                    <span style={{ color: '#48bb78' }}>{c.completed_bookings}</span>
                  ),
                },
                {
                  key: 'completed_revenue',
                  label: 'Revenue',
                  align: 'right',
                  render: (c: any) =>
                    `$${Number(c.completed_revenue).toLocaleString('en-AU', { minimumFractionDigits: 2 })}`,
                },
                {
                  key: 'forecast_revenue',
                  label: 'Forecast',
                  align: 'right',
                  render: (c: any) => (
                    <span style={{ color: 'var(--color-accent)' }}>
                      $
                      {Number(c.forecast_revenue ?? 0).toLocaleString('en-AU', {
                        minimumFractionDigits: 2,
                      })}
                    </span>
                  ),
                },
                {
                  key: 'google_ad_clicks',
                  label: 'Google Ads',
                  align: 'right',
                  render: (c: any) => c.google_ad_clicks,
                },
              ]}
              emptyMessage="No campaigns match the current filter."
            />
          </div>

          {/* Google Ads booking heatmap — day of week × hour of day */}
          <div style={{ ...cardStyle, marginBottom: 'var(--spacing-4)' }}>
            <h2
              style={{
                fontFamily: 'var(--font-family-heading)',
                fontSize: 'var(--font-size-md)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              Google Ads — Booking Heatmap
            </h2>
            <p
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              When customers from Google Ads are creating bookings (Brisbane time). Darker = more
              bookings in that day/hour slot.
            </p>
            {heatmapMax === 0 ? (
              <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
                No Google Ads bookings in this period.
              </p>
            ) : (
              <div className="scroll-hint">
                <span className="sr-only">Scroll horizontally for more hours</span>
                <table style={{ borderCollapse: 'separate', borderSpacing: 2 }}>
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}></th>
                      {Array.from({ length: 24 }).map((_, h) => (
                        <th
                          key={h}
                          style={{
                            fontSize: 10,
                            color: 'var(--color-text-muted)',
                            fontWeight: 500,
                            width: 22,
                            textAlign: 'center',
                          }}
                        >
                          {h % 6 === 0
                            ? h === 0
                              ? '12a'
                              : h === 12
                                ? '12p'
                                : `${h % 12}${h < 12 ? 'a' : 'p'}`
                            : ''}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((label, dowIdx) => {
                      // EXTRACT(DOW) returns 0=Sunday..6=Saturday, matches our display order
                      return (
                        <tr key={dowIdx}>
                          <td
                            style={{
                              fontSize: 11,
                              color: 'var(--color-text-muted)',
                              fontWeight: 600,
                              textAlign: 'right',
                              paddingRight: 4,
                            }}
                          >
                            {label}
                          </td>
                          {heatmapMatrix[dowIdx].map((count, hour) => {
                            const intensity = heatmapMax > 0 ? count / heatmapMax : 0;
                            // Blue scale, matches the reference look
                            const bg =
                              count === 0
                                ? 'var(--color-background)'
                                : `rgba(66, 133, 244, ${0.15 + intensity * 0.85})`;
                            return (
                              <td
                                key={hour}
                                title={`${label} ${hour}:00 — ${count} booking${count === 1 ? '' : 's'}`}
                                style={{
                                  width: 22,
                                  height: 22,
                                  backgroundColor: bg,
                                  borderRadius: 3,
                                  border:
                                    count === 0
                                      ? '1px solid var(--color-border)'
                                      : '1px solid transparent',
                                }}
                              />
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div
                  style={{
                    marginTop: 'var(--spacing-2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    fontSize: 11,
                    color: 'var(--color-text-muted)',
                  }}
                >
                  <span>Less</span>
                  {[0.15, 0.35, 0.55, 0.75, 1].map((v) => (
                    <span
                      key={v}
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: 3,
                        backgroundColor: `rgba(66, 133, 244, ${v})`,
                      }}
                    />
                  ))}
                  <span>More</span>
                  <span style={{ marginLeft: 'var(--spacing-3)' }}>
                    Peak: {heatmapMax} booking{heatmapMax === 1 ? '' : 's'} in a single hour
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Recent Bookings */}
          <div style={cardStyle}>
            <h2
              style={{
                fontFamily: 'var(--font-family-heading)',
                fontSize: 'var(--font-size-md)',
                marginBottom: 'var(--spacing-3)',
              }}
            >
              Recent Ad-Attributed Bookings
            </h2>
            <ResponsiveList
              rows={filteredBookings}
              getRowKey={(b: any) => b.id}
              columns={[
                {
                  key: 'customer',
                  label: 'Customer',
                  primaryOnMobile: true,
                  render: (b: any) => b.customer_name || 'Guest',
                },
                {
                  key: 'booked_on',
                  label: 'Booked On',
                  render: (b: any) =>
                    new Date(b.created_at).toLocaleString('en-AU', {
                      timeZone: 'Australia/Brisbane',
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    }),
                },
                {
                  key: 'appointment',
                  label: 'Appointment',
                  render: (b: any) =>
                    b.start_time
                      ? new Date(b.start_time).toLocaleString('en-AU', {
                          timeZone: 'Australia/Brisbane',
                          day: 'numeric',
                          month: 'short',
                          hour: 'numeric',
                          minute: '2-digit',
                          hour12: true,
                        })
                      : '—',
                },
                { key: 'location', label: 'Location', render: (b: any) => b.location_name },
                { key: 'barber', label: 'Barber', render: (b: any) => b.barber_name },
                {
                  key: 'status',
                  label: 'Status',
                  render: (b: any) => (
                    <span
                      style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: 'var(--radius-default)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                        backgroundColor: `${statusColor[b.status] || 'var(--color-border)'}22`,
                        color: statusColor[b.status] || 'var(--color-text-muted)',
                      }}
                    >
                      {b.status}
                    </span>
                  ),
                },
                {
                  key: 'price',
                  label: 'Price',
                  align: 'right',
                  render: (b: any) => `$${Number(b.total_price).toFixed(2)}`,
                },
                {
                  key: 'source',
                  label: 'Source',
                  render: (b: any) =>
                    `${b.utm_source || '—'}${b.utm_medium ? ` / ${b.utm_medium}` : ''}`,
                },
                {
                  key: 'campaign_name',
                  label: 'Campaign',
                  render: (b: any) => b.utm_campaign || '—',
                },
                {
                  key: 'gclid',
                  label: 'gclid',
                  render: (b: any) => (b.gclid ? '✓' : '—'),
                  hideOnMobile: true,
                },
              ]}
              emptyMessage="No bookings match the current filter."
            />
          </div>
        </>
      )}
    </div>
  );
}
